'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import quickFetch from 'quick-fetch';
import { createAction } from 'flux-actions';
import { createRequestAction, createRequestStatusMeta } from 'MeetingsBase/utils/actions';
import getIn from 'transmute/getIn';
import * as ActionTypes from 'MeetingsLib/redux/actions/ActionTypes';
import { Map as ImmutableMap } from 'immutable';
import { showUserMessage } from './UIActions';
import { fetchPublicAvailabilityInfo as fetchPublicAvailabilityInfoRequest, fetchMeetingsUser as fetchMeetingsUserRequest, fetchCompanyName as fetchCompanyNameRequest, createMeeting as createMeetingRequest, cancelMeeting as cancelMeetingRequest, fetchEventDetails as fetchEventDetailsRequest, rescheduleMeeting as rescheduleMeetingRequest } from 'MeetingsLib/api/bookingAPI';
import userInfo from 'hub-http/userInfo';
import { maybeRedirectToHubSpotDomain } from 'MeetingsLib/utils/utils';
import { setFormState } from './FormActions';
import { shouldFetchAvailability, getUserProfile as getUserProfileSelector } from 'MeetingsLib/selectors/BookInfoSelectors';
import { getInitialFormState } from 'MeetingsLib/selectors/FormFieldValidationSelector';
import { canSubmitInitialPropertyForm as canSubmitInitialPropertyFormSelector } from 'MeetingsLib/selectors/AutoSubmitSelector';
import { getFetchAvailabilityQueryParams, getCreateMeetingQueryParams, getCreateMeetingBodyData, getEmbedded, buildMeetingRescheduleRequestBody } from 'MeetingsLib/selectors/RESTParamSelectors';
import { isSubmittingMeeting } from 'MeetingsLib/selectors/RequestStatusSelectors';
import { getDisablePrefill } from 'MeetingsLib/selectors/PrefillSelectors';
import { shouldAllowTroubleshootMode } from 'MeetingsLib/selectors/TroubleshootingSelectors';
import PublicMeetingsUser from 'MeetingsBase/models/PublicMeetingsUser';
import PublicAvailabilityInfo from 'MeetingsBase/models/PublicAvailabilityInfo';
import FormState from 'MeetingsLib/models/FormState';
import TimeRange from 'MeetingsBase/models/TimeRange';
import UserProfile from 'MeetingsBase/models/UserProfile';
import Branding from 'MeetingsBase/models/Branding';
import { BOOKING_TIME_CONFLICT } from 'MeetingsLib/constants/errors';
import { LINK_TYPES, MEETING_TYPES } from 'MeetingsBase/constants/enums';
import { STATUS } from 'MeetingsLib/constants/status';
import I18n from 'I18n';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import LinkAvailability from 'MeetingsBase/models/LinkAvailability';
export var logMeetingCreation = createAction(ActionTypes.LOG_MEETING_CREATE);
export var fetchPublicAvailabilityInfoAction = createRequestAction(ActionTypes.PUBLIC_AVAILABILITY_INFO_FETCH_STARTED);
export var fetchPublicAvailabilityInfoSucceededAction = createRequestAction(ActionTypes.PUBLIC_AVAILABILITY_INFO_FETCH_SUCCEEDED, function (res) {
  var bookInfo = PublicAvailabilityInfo.from(res);
  var postResponse = {
    customFormFields: FormState.customFieldsFromBookInfo(res),
    bookInfo: bookInfo,
    meetingsUser: PublicMeetingsUser.fromBookInfo(bookInfo),
    branding: Branding.from(res.brandingMetadata)
  };

  if (window.newrelic) {
    var hasMeetingsUser = !!postResponse.meetingsUser;
    window.newrelic.setCustomAttribute('bookInfoHasMeetingsUser', hasMeetingsUser);

    if (hasMeetingsUser) {
      window.newrelic.setCustomAttribute('calendarProvider', postResponse.meetingsUser.calendarProvider);
    }
  }

  return postResponse;
});
export var fetchPublicAvailabilityInfoSucceeded = function fetchPublicAvailabilityInfoSucceeded(response, changeLocale) {
  return function (dispatch, getState) {
    var dispatchSuccess = function dispatchSuccess() {
      var state = getState();
      var disablePrefill = getDisablePrefill(state);

      if (disablePrefill) {
        response.formPrefills = {};
      }

      dispatch(fetchPublicAvailabilityInfoSucceededAction(response)); // get new state after dispatch above; see https://git.hubteam.com/HubSpot/MeetingsUI/pull/2367 for context

      state = getState();
      var initialFormState = getInitialFormState(state);
      var shouldSetFormState = canSubmitInitialPropertyFormSelector(state);

      if (initialFormState && shouldSetFormState) {
        dispatch(setFormState(initialFormState));
      }
    };

    var locale = response.customParams.locale;
    var language = response.customParams.language;

    if (locale) {
      if (changeLocale) {
        changeLocale(locale, language).then(dispatchSuccess);
      } else {
        // legacy I18n locale change API - to be replaced by I18n Providers
        I18n.langEnabled = language !== 'en';
        I18n.changeLocale(locale).then(dispatchSuccess);
      }
    } else {
      dispatchSuccess();
    }
  };
};
export var fetchPublicAvailabilityInfoFailed = createRequestAction(ActionTypes.PUBLIC_AVAILABILITY_INFO_FETCH_FAILED, function (error) {
  return {
    error: error
  };
});
export var fetchPublicAvailabilityInfoForOwnerAction = createRequestAction(ActionTypes.PUBLIC_AVAILABILITY_INFO_OWNER_FETCH_STARTED);
export var fetchPublicAvailabilityInfoForOwnerSucceeded = createRequestAction(ActionTypes.PUBLIC_AVAILABILITY_INFO_OWNER_FETCH_SUCCEEDED, function (response) {
  var postResponse = {
    customFormFields: FormState.customFieldsFromBookInfo(response),
    bookInfo: PublicAvailabilityInfo.from(response)
  };
  return postResponse;
});
export var fetchPublicAvailabilityInfoForOwnerFailed = createRequestAction(ActionTypes.PUBLIC_AVAILABILITY_INFO_OWNER_FETCH_FAILED, function (error) {
  return {
    error: error
  };
});

var fetchWithEarlyRequestHook = function fetchWithEarlyRequestHook(fallbackFetch, requestName) {
  var earlyRequest = quickFetch.getRequestStateByName(requestName);

  if (earlyRequest) {
    return new Promise(function (resolve, reject) {
      earlyRequest.whenFinished(function () {
        var mark = requestName + "-finished";

        if (window.performance && window.performance.mark && window.performance.measure) {
          window.performance.mark(mark);
          window.performance.measure(requestName + "-after-mount", 'create-booking-component', mark);
        }

        resolve.apply(void 0, arguments);
      });
      earlyRequest.onError(function () {
        return reject.apply(void 0, arguments);
      });
    });
  }

  return fallbackFetch();
};

export var fetchMeetingsUserStarted = createRequestAction(ActionTypes.MEETINGS_USER_FETCH_STARTED);
export var fetchMeetingsUserSucceeded = createRequestAction(ActionTypes.MEETINGS_USER_FETCH_SUCCEEDED, function (response) {
  var meetingsUser = PublicMeetingsUser.from(response);

  if (window.newrelic) {
    window.newrelic.setCustomAttribute('calendarProvider', meetingsUser.calendarProvider);
  }

  return {
    meetingsUser: meetingsUser
  };
});
export var fetchMeetingsUserFailed = createRequestAction(ActionTypes.MEETINGS_USER_FETCH_FAILED, function (error) {
  return {
    error: error
  };
});

function fetchMeetingsUser() {
  return function (dispatch, getState, httpClient) {
    var slug = getState().userSlug;
    dispatch(fetchMeetingsUserStarted());
    return fetchMeetingsUserRequest(httpClient, slug, true).then(function (response) {
      dispatch(fetchMeetingsUserSucceeded(response));
    }, function (response) {
      dispatch(fetchMeetingsUserFailed(response));
    });
  };
}

function fetchPortalAuth(portalId) {
  return function (dispatch, getState) {
    var state = getState();
    var shouldAllowTroubleshoot = shouldAllowTroubleshootMode(state);
    var embedded = getEmbedded(state);

    if (shouldAllowTroubleshoot && !embedded) {
      userInfo({
        portalId: portalId,
        ignoreRedirect: true,
        hubletOverride: window.hubspot.meetingsHubletOverride
      }).then(function (res) {
        dispatch({
          type: ActionTypes.PORTAL_AUTH_SUCCEEDED,
          payload: res
        });
      }, function () {});
    }
  };
}

var createPublicAvailabilityFetcher = function createPublicAvailabilityFetcher(dispatch, getState, httpClient, params) {
  var state = getState();
  var queryParams = getFetchAvailabilityQueryParams(params)(state);
  return function () {
    return fetchPublicAvailabilityInfoRequest(httpClient, queryParams);
  };
};

export function fetchPublicAvailabilityInfo(params, changeLocale) {
  return function (dispatch, getState, httpClient) {
    dispatch(fetchPublicAvailabilityInfoAction());
    var fetch = createPublicAvailabilityFetcher(dispatch, getState, httpClient, params);
    var fetchPromise = fetchWithEarlyRequestHook(fetch, 'book-info');
    return fetchPromise.then(function (response) {
      var bookInfo = PublicAvailabilityInfo.from(response);
      var meetingsUser = PublicMeetingsUser.fromBookInfo(bookInfo);
      var portalId = bookInfo.portalId;
      dispatch(fetchPortalAuth(portalId));

      if (!meetingsUser) {
        return fetchMeetingsUser()(dispatch, getState, httpClient).then(function () {
          dispatch(fetchPublicAvailabilityInfoSucceeded(response, changeLocale));
          return bookInfo;
        });
      } else {
        dispatch(fetchPublicAvailabilityInfoSucceeded(response, changeLocale));
        return bookInfo;
      }
    }, function (error) {
      dispatch(fetchPublicAvailabilityInfoFailed(error)); // redirect users who try to hit meeting links on
      // www.hubspot.com when they are not hosted there

      if (error && error.status === STATUS.NOT_FOUND) {
        maybeRedirectToHubSpotDomain(window.location.host);
      }

      throw error;
    });
  };
}
export function fetchPublicAvailabilityInfoForOwner(params) {
  return function (dispatch, getState, httpClient) {
    dispatch(fetchPublicAvailabilityInfoForOwnerAction());
    var fetch = createPublicAvailabilityFetcher(dispatch, getState, httpClient, params);
    return fetch().then(function (response) {
      dispatch(fetchPublicAvailabilityInfoForOwnerSucceeded(response));
      return PublicAvailabilityInfo.from(response);
    }, function (error) {
      dispatch(fetchPublicAvailabilityInfoForOwnerFailed(error));
      return Promise.reject(error);
    });
  };
}
export var fetchCompanyNameAction = createRequestAction(ActionTypes.COMPANY_NAME_FETCH_STARTED);
export var fetchCompanyNameSucceeded = createRequestAction(ActionTypes.COMPANY_NAME_FETCH_SUCCEEDED, function (response) {
  return response.companyName;
});
export var fetchCompanyNameFailed = createRequestAction(ActionTypes.COMPANY_NAME_FETCH_FAILED, function (error) {
  return {
    error: error
  };
});
export function fetchCompanyName() {
  return function (dispatch, getState, httpClient) {
    dispatch(fetchCompanyNameAction());
    var slug = encodeURIComponent(getState().userSlug);
    return fetchCompanyNameRequest(httpClient, slug).then(function (response) {
      dispatch(fetchCompanyNameSucceeded(response));
      return response;
    }, function (error) {
      dispatch(fetchCompanyNameFailed(error));
    });
  };
}
export function getAvailability(changeLocale) {
  return function (dispatch, getState) {
    if (shouldFetchAvailability(getState())) {
      var params = {
        includeDeleted: 'true'
      };
      dispatch(fetchPublicAvailabilityInfo(params, changeLocale)).then(function (bookInfo) {
        if (bookInfo && bookInfo.linkType === LINK_TYPES.get(MEETING_TYPES.ROUND_ROBIN) && !getIn(['customParams', 'displayInfo', 'headline'], bookInfo)) {
          dispatch(fetchCompanyName());
        }
      }, function (error) {
        if (error && error.status === 404) {
          return dispatch(fetchMeetingsUser());
        }

        throw error;
      });
    }

    return Promise.resolve();
  };
}
export var createMeetingAction = createRequestAction(ActionTypes.MEETING_CREATE_STARTED);
export var createMeetingReset = createAction(ActionTypes.MEETING_CREATE_RESET, null, function () {
  return createRequestStatusMeta(ActionTypes.MEETING_CREATE_UNINITIALIZED);
});
export var createMeetingSucceeded = createRequestAction(ActionTypes.MEETING_CREATE_SUCCEEDED, function (response) {
  var postResponse = new ImmutableMap({
    timerange: TimeRange.from({
      start: response.start,
      end: response.end
    }),
    organizer: UserProfile.from(response.organizer)
  });
  return postResponse;
});
export var createMeetingFailed = createRequestAction(ActionTypes.MEETING_CREATE_FAILED, function (error) {
  return {
    error: error
  };
});
var bookingTimeConflict = createAction(ActionTypes.BOOKING_TIME_CONFLICT, function (linkAvailability) {
  return {
    linkAvailability: LinkAvailability.from(linkAvailability)
  };
}, function () {
  return createRequestStatusMeta(ActionTypes.MEETING_CREATE_UNINITIALIZED);
});
export function createMeeting(event) {
  return function (dispatch, getState, httpClient) {
    dispatch(createMeetingAction());
    var state = getState();
    var data = getCreateMeetingBodyData(event)(state);
    var queryParams = getCreateMeetingQueryParams(state);
    return createMeetingRequest(httpClient, queryParams, data).then(function (response) {
      dispatch(createMeetingSucceeded(response));
      return response;
    }, function (error) {
      if (error.status === 400 && error.responseJSON && error.responseJSON.errorType === 'TIME_SLOT_NOT_AVAILABLE') {
        dispatch(bookingTimeConflict(error.responseJSON.linkAvailability));
        throw Error(BOOKING_TIME_CONFLICT);
      }

      dispatch(createMeetingFailed(error));
      throw error;
    });
  };
}
export var meetingRescheduleStartedAction = createRequestAction(ActionTypes.MEETING_RESCHEDULE_STARTED);
export var meetingRescheduleSucceededAction = createRequestAction(ActionTypes.MEETING_RESCHEDULE_SUCCEEDED, function (requestBody) {
  var postResponse = new ImmutableMap({
    timerange: TimeRange.from({
      start: requestBody.startTime,
      end: requestBody.endTime
    })
  });
  return postResponse;
});
export var meetingRescheduleFailedAction = createRequestAction(ActionTypes.MEETING_RESCHEDULE_FAILED, function (error) {
  return {
    error: error
  };
});
export var rescheduleMeeting = function rescheduleMeeting(event) {
  return function (dispatch, getState, httpClient) {
    var state = getState();
    dispatch(meetingRescheduleStartedAction());
    var meetingRescheduleRequestBody = buildMeetingRescheduleRequestBody(event)(state);
    rescheduleMeetingRequest(httpClient, meetingRescheduleRequestBody).then(function () {
      dispatch(meetingRescheduleSucceededAction(meetingRescheduleRequestBody));
    }, function (error) {
      // Trigger error component to allow users to fallback to booking a new meeting
      dispatch(meetingRescheduleFailedAction(error));
    });
  };
};
export var bookMeeting = function bookMeeting() {
  return function (dispatch, getState) {
    var state = getState();
    var isSalesStarter = state.isSalesStarter,
        calendarProvider = state.calendarProvider,
        linkType = state.bookInfo.linkType,
        event = state.bookingLifecycle.event;

    var _getUserProfileSelect = getUserProfileSelector(state),
        email = _getUserProfileSelect.email;

    var submittingMeeting = isSubmittingMeeting(state);

    if (submittingMeeting) {
      return null;
    }

    return dispatch(createMeeting(event)).then(function () {
      dispatch(logMeetingCreation({
        integration: calendarProvider,
        linkType: linkType,
        isSalesStarter: isSalesStarter
      }));
    }, function (error) {
      if (error.message === BOOKING_TIME_CONFLICT) {
        dispatch(showUserMessage({
          type: 'info',
          content: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
            message: "errorMessages.bookedFromUnder"
          })
        }));
      } else {
        dispatch(showUserMessage({
          type: 'danger',
          content: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
            message: "errorMessages.tryAgain",
            options: {
              email: email
            }
          })
        }));
      }
    });
  };
};
export var fetchEventDetailsSucceededAction = createRequestAction(ActionTypes.FETCH_EVENT_DETAILS_SUCCEEDED, function (res) {
  return res;
});
export var fetchEventDetailsFailedAction = createRequestAction(ActionTypes.FETCH_EVENT_DETAILS_FAILED);
export var fetchEventDetails = function fetchEventDetails(slug, meetingChangeId) {
  return function (dispatch, getState, httpClient) {
    return fetchEventDetailsRequest(httpClient, slug, meetingChangeId).then(function (eventDetails) {
      dispatch(fetchEventDetailsSucceededAction(eventDetails));
      return eventDetails;
    }, function (err) {
      dispatch(fetchEventDetailsFailedAction(err));
      return err;
    });
  };
};
export var cancelMeetingAction = createRequestAction(ActionTypes.MEETING_CANCEL_STARTED);
export var cancelMeetingSucceededAction = createRequestAction(ActionTypes.MEETING_CANCEL_SUCCEEDED);
export var cancelMeetingFailedAction = createRequestAction(ActionTypes.MEETING_CANCEL_FAILED);
export var cancelMeeting = function cancelMeeting(slug, meetingChangeId) {
  return function (dispatch, getState, httpClient) {
    dispatch(cancelMeetingAction());
    return cancelMeetingRequest(httpClient, slug, meetingChangeId).then(function () {
      dispatch(cancelMeetingSucceededAction());
    }, function () {
      dispatch(cancelMeetingFailedAction());
    });
  };
};